import {create} from '@github-ui/dotcom-schema/protobuf'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {
  type CompletedReview,
  CompletedReviewSchema,
  DeferredType,
  type PendingReviewRequest,
  PendingReviewRequestSchema,
  type ReviewRequestsResponse,
  ReviewRequestsResponseSchema,
  ReviewState,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {
  legacyReviewerToProto,
  type LegacyCompletedReview,
  type LegacyPendingReviewRequest,
  type LegacyReviewRequestsData,
} from '../page-data/payloads/review-requests'

// Temporary legacy -> proto shim for the review_requests page-data endpoint.
// With the `review_requests_proto` flag off the backend still returns the legacy
// JSON; this converts it up to the canonical proto shape so consumers only ever
// observe the proto type. Once the flag is fully rolled out (backend only emits
// proto3-JSON) this whole module and its use in the route can be deleted.

const REVIEW_STATE: Record<string, ReviewState> = {
  pending: ReviewState.PENDING,
  commented: ReviewState.COMMENTED,
  approved: ReviewState.APPROVED,
  changes_requested: ReviewState.CHANGES_REQUESTED,
  dismissed: ReviewState.DISMISSED,
}

const DEFERRED_TYPE: Record<string, DeferredType> = {
  codeowner: DeferredType.CODEOWNER,
  copilot: DeferredType.COPILOT,
}

function completedReviewToProto(review: LegacyCompletedReview): CompletedReview {
  return create(CompletedReviewSchema, {
    id: BigInt(review.id),
    reviewer: legacyReviewerToProto(review.reviewer),
    state: REVIEW_STATE[review.state] ?? ReviewState.UNSPECIFIED,
    headSha: review.headSha,
    submittedAt: review.submittedAt,
    hasWriteAccess: review.hasWriteAccess,
    canReRequest: review.canReRequest,
    onBehalfOfTeams: review.onBehalfOfTeams.map(legacyReviewerToProto),
  })
}

function pendingReviewRequestToProto(request: LegacyPendingReviewRequest): PendingReviewRequest {
  return create(PendingReviewRequestSchema, {
    reviewer: legacyReviewerToProto(request.reviewer),
    isCodeOwner: request.isCodeOwner,
    isCodeOwnerFromDelegation: request.isCodeOwnerFromDelegation,
    deferredType: request.deferredType
      ? (DEFERRED_TYPE[request.deferredType] ?? DeferredType.UNSPECIFIED)
      : DeferredType.UNSPECIFIED,
    delegatedFromTeam: request.delegatedFromTeam ? legacyReviewerToProto(request.delegatedFromTeam) : undefined,
    createdAt: request.createdAt ?? '',
  })
}

/** Converts the legacy (flag-off) review_requests JSON up to the proto shape. */
export function normalizeReviewRequestsResponse(data: LegacyReviewRequestsData): ReviewRequestsResponse {
  return create(ReviewRequestsResponseSchema, {
    pendingReviewRequests: data.pendingReviewRequests.map(pendingReviewRequestToProto),
    completedReviews: data.completedReviews.map(completedReviewToProto),
  })
}

/**
 * Fetches review_requests, decoding proto3-JSON via the schema when the backend
 * sends it (flag on) and converting the legacy JSON up to the proto shape
 * otherwise (flag off), so the consumer always receives a ReviewRequestsResponse.
 */
export async function reviewRequestsQueryFn(
  queryKey: Parameters<typeof queryFnFetch>[0],
): Promise<ReviewRequestsResponse> {
  const response = await queryFnFetch<typeof ReviewRequestsResponseSchema, LegacyReviewRequestsData>(queryKey, {
    schema: ReviewRequestsResponseSchema,
  })
  // Proto-decoded responses carry `$typeName`; legacy JSON does not.
  return '$typeName' in response ? response : normalizeReviewRequestsResponse(response)
}
